import { RequestUtil } from '../common';
import { Message, WPPError } from './common-objects';
import type {
    EntrustWPPConfig,
    EntrustWPPIssuerConfig,
    EntrustWPPGoogleConfig,
    EntrustWPPAppleConfig,
    GetPushOpaqueData,
    OnResult,
    OnFailure,
    OnCancel,
} from './common-objects';
import { EntrustWebPushProvisionningGoogle } from './google/web-push-provisioning-google';
import { EntrustWebPushProvisionningApple } from './apple/web-push-provisioning-apple';

// Build-time injected by Rollup via @rollup/plugin-replace.
// `process.env.NODE_ENV` is statically replaced with "development" or "production".
declare const process: { env: { NODE_ENV: string } };

// Re-export types for external use
export type {
    EntrustWPPConfig,
    EntrustWPPIssuerConfig,
    EntrustWPPGoogleConfig,
    EntrustWPPAppleConfig,
    GetPushOpaqueData,
    OnResult,
    OnFailure,
    OnCancel,
} from './common-objects';
export { Message, MessageOutput, WPPError } from './common-objects';

export class EntrustWebPushProvisionning {
    issuerConfig: EntrustWPPIssuerConfig;
    config: EntrustWPPConfig;
    privateKey: object;
    publicKey: object;
    keyId: string;
    issuerPushSessionId: string;

    getPushOpaqueDataPromise: GetPushOpaqueData;

    onResult: OnResult;
    onFailure: OnResult;
    onCancel?: OnResult;

    constructor(
        config: EntrustWPPConfig,
        getPushOpaqueDataPromise: GetPushOpaqueData,
        onResult: OnResult,
        onFailure: OnResult,
        onCancel?: OnResult
    ) {
        this.config = config;
        this.getPushOpaqueDataPromise = getPushOpaqueDataPromise;
        this.onResult = onResult;
        this.onFailure = onFailure;
        this.onCancel = onCancel;

        this.getIssuerConfigFile(config.issuerId).then(async (issuerConfig) => {
            this.issuerConfig = issuerConfig;
            this.checkConfig();
            await this.init();
        });
    }

    private checkConfig() {
        if (this.config.type === 'google' && !this.issuerConfig.googleEnabled) {
            this.onFailure(
                new WPPError(Message.TECHNICAL_ERROR, `Google is not available for issuerId : ${this.config.issuerId}`)
            );
            throw new Error(`Google is not available for issuerId : ${this.config.issuerId}`);
        } else if (this.config.type === 'apple' && !this.issuerConfig.appleEnabled) {
            this.onFailure(
                new WPPError(Message.TECHNICAL_ERROR, `Apple is not available for issuerId : ${this.config.issuerId}`)
            );
            throw new Error(`Apple is not available for issuerId : ${this.config.issuerId}`);
        }
    }

    private async init(): Promise<void> {
        if (this.config.type === 'google') {
            const google = new EntrustWebPushProvisionningGoogle(
                this.config,
                this.issuerConfig,
                this.getPushOpaqueDataPromise,
                this.onResult,
                this.onFailure,
                this.onCancel
            );
            await google.init();
        } else if (this.config.type === 'apple') {
            const apple = new EntrustWebPushProvisionningApple(
                this.config,
                this.issuerConfig,
                this.getPushOpaqueDataPromise,
                this.onResult,
                this.onFailure,
                this.onCancel
            );
            await apple.init();
        }
    }

    /**
     * Retrieve issuer configuration file.
     * Configuration files are stored on the same host of the web-push-provisioning script but in a different folder.
     * - In development: file is located at <scriptHost>/config/config-<issuerId>.json
     * - In production: file name is the SHA-256 hex digest of the issuerId,
     *   located at <scriptHost>/config/<sha256(issuerId)>.json
     * @param {string} issuerId The issuerId
     * @returns {Promise<EntrustWPPIssuerConfig>} The issuer configuration as an object
     */
    async getIssuerConfigFile(issuerId: string): Promise<EntrustWPPIssuerConfig> {
        try {
            const fileName =
                process.env.NODE_ENV === 'production'
                    ? `webpush/config/${await sha256Hex(issuerId)}.json`
                    : `config/config-${issuerId}.json`;
            const response = await RequestUtil.getResource('web-push-provisioning.js', `${fileName}`, 'data-wpp');
            const configText = await response.text();
            return JSON.parse(configText) as EntrustWPPIssuerConfig;
        } catch (error) {
            this.onFailure(
                new WPPError(Message.TECHNICAL_ERROR, `Configuration file not found for issuerId: ${issuerId}`)
            );
            throw error;
        }
    }
}

/**
 * Compute the SHA-256 hex digest of the given string using the Web Crypto API.
 * Used in production to derive the obfuscated issuer configuration filename.
 */
async function sha256Hex(input: string): Promise<string> {
    const data = new TextEncoder().encode(input);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(hashBuffer))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
}
